import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { ChangeInputHandler } from '../../../types';
import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { MIN_POSSIBLE_STAKE_PRICE } from 'app/constants/userRestrictions';
import { Button, ButtonIntent, ButtonSize, ButtonType } from 'app/design-system/components/Button';
import { useTranslation } from 'app/lang';

import {
  UpdateOneClickBetAmountPreference,
  updateOneClickBetAmountPreference,
} from '../../../actions/oneClickBet';
import { SubmissionState } from '../../../constants';
import {
  selectHasOneClickBetEnoughMoney,
  selectOneClickBetAmount,
  selectProcessingEntry,
} from '../../../selectors/oneClickBet';
import { selectSubmission } from '../../../selectors/submission';
import { PresetWithDisplayType } from '../../../types/oneClickBet';
import AmountInput from '../../AmountInputByAccountType/components/AmountInput';
import Error from '../../EntryComponents/Error';
import NoMoneyLink from '../../NoMoneyLink';

import dictionary from './Preferences.dictionary.json';

import styles from './Preferences.module.scss';

// ---

type StoreProps = {
  amount: PresetWithDisplayType;
  processing: boolean;
  hasEnoughMoney: boolean;
  disabled: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<UpdateOneClickBetAmountPreference>;
};

const DEFAULT_VALUE = '0';

const OneClickBetPreferences: FC<StoreProps & DispatchProp> = ({
  amount,
  processing,
  hasEnoughMoney,
  disabled,
  dispatch,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(amount.value?.toString(10) || DEFAULT_VALUE);
  const numericValue = parseFloat(value);

  const _inputClassName = classNames(styles.inputContainer, {
    [styles.disabled]: disabled,
    [styles.error]: !hasEnoughMoney,
  });

  const changeValueHandler: ChangeInputHandler = useCallback(({ value }) => {
    setValue(value);
  }, []);

  const saveAmountHandler = useCallback(() => {
    dispatch(updateOneClickBetAmountPreference({ ...amount, value: numericValue }));
  }, [numericValue, amount]);

  return (
    <div className={classNames(styles.container, { [styles.processing]: processing })}
         data-test="oneClickBetPreferences-container">
      <div className={styles.controls}
           data-test="oneClickBetPreferences-controls">
        <div className={_inputClassName}
             data-test="oneClickBetPreferences-inputContainer">
          <AmountInput value={value}
                       disableDebounce
                       inputClassName={styles.input}
                       unitsClassName={styles.units}
                       onChange={changeValueHandler} />
        </div>
        <Button type={ButtonType.SUBMIT}
                intent={ButtonIntent.PRIMARY}
                size={ButtonSize.SMALL}
                data-test="oneClickBetPreferences-saveButton"
                onClick={saveAmountHandler}>
          {t(dictionary.save)}
        </Button>
      </div>

      {!hasEnoughMoney && (
        <NoMoneyLink className={styles.noMoney} />
      )}

      {!Number.isNaN(amount.value) && amount.value <= MIN_POSSIBLE_STAKE_PRICE && (
        <Error className={styles.errorMessage}
               data-test="oneClickBetPreferences-error"
               message={t(dictionary.errorMessage, { amount: 0 })} />
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  amount: selectOneClickBetAmount(state),
  processing: selectProcessingEntry(state) !== null,
  hasEnoughMoney: selectHasOneClickBetEnoughMoney(state),
  disabled: selectSubmission(state) !== SubmissionState.AWAITING,
});

export default connect(mapStateToProps)(OneClickBetPreferences);
